import type { CameraLike, Track } from './types';
import { Vec3, type Vec3Like } from './vector';

export interface SampleAnimTrackRouteOptions {
  samples?: number;
  fallbackFov?: number;
}

export interface SampledAnimTrackRoute {
  route: CameraLike[];
  keyframes: CameraLike[];
}

const DEFAULT_ROUTE_SAMPLES = 96;
const DEFAULT_FOV = 60;

export function sampleAnimTrackRoute(
  track: Track,
  options: SampleAnimTrackRouteOptions = {},
): SampledAnimTrackRoute {
  const times = track.keyframes.times;
  // 优先使用 markerTimes（原始用户关键帧）绘制标记，避免 densifyLookFlips 插入的过渡帧显示为标记
  const markerTimes = track.markerTimes ?? times;
  const keyframes = markerTimes.map((time) => sampleCameraAtTime(track, time, options.fallbackFov));
  if (keyframes.length === 0) return { route: [], keyframes };
  if (keyframes.length === 1) return { route: keyframes, keyframes };

  const duration = resolveTrackDuration(track);
  const samples = Math.max(2, Math.floor(options.samples ?? DEFAULT_ROUTE_SAMPLES));
  const route: CameraLike[] = [];

  for (let i = 0; i < samples; i += 1) {
    const t = duration * (i / (samples - 1));
    route.push(sampleCameraAtTime(track, t, options.fallbackFov));
  }

  return { route, keyframes };
}

function resolveTrackDuration(track: Track): number {
  const lastTime = track.keyframes.times.at(-1) ?? 0;
  return Math.max(track.duration, lastTime, 0);
}

function sampleCameraAtTime(track: Track, time: number, fallbackFov?: number): CameraLike {
  const times = track.keyframes.times;
  if (times.length <= 1 || time <= times[0]) return cameraAtKeyframe(track, 0, fallbackFov);

  const lastIndex = times.length - 1;
  if (time >= times[lastIndex]) return cameraAtKeyframe(track, lastIndex, fallbackFov);

  const nextIndex = times.findIndex((value) => value > time);
  const rightIndex = nextIndex === -1 ? lastIndex : nextIndex;
  const leftIndex = Math.max(0, rightIndex - 1);
  if (track.interpolation === 'step') return cameraAtKeyframe(track, leftIndex, fallbackFov);

  const span = Math.max(times[rightIndex] - times[leftIndex], 1e-6);
  const alpha = (time - times[leftIndex]) / span;
  const left = cameraAtKeyframe(track, leftIndex, fallbackFov);
  const right = cameraAtKeyframe(track, rightIndex, fallbackFov);

  return {
    position: lerpVec(left.position, right.position, alpha),
    target: lerpVec(left.target, right.target, alpha),
    fov: lerpNumber(
      left.fov ?? fallbackFov ?? DEFAULT_FOV,
      right.fov ?? fallbackFov ?? DEFAULT_FOV,
      alpha,
    ),
    up:
      left.up && right.up
        ? normalizeVec3(lerpVec(left.up, right.up, alpha))
        : (left.up ?? right.up),
  };
}

function cameraAtKeyframe(track: Track, index: number, fallbackFov?: number): CameraLike {
  const values = track.keyframes.values;
  return {
    position: vecAt(values.position, index),
    target: vecAt(values.target, index),
    fov: values.fov?.[index] ?? fallbackFov ?? DEFAULT_FOV,
    up: values.up ? vecAt(values.up, index) : undefined,
  };
}

function vecAt(values: number[], index: number): Vec3Like {
  const offset = index * 3;
  return {
    x: values[offset] ?? 0,
    y: values[offset + 1] ?? 0,
    z: values[offset + 2] ?? 0,
  };
}

function lerpVec(a: Vec3Like, b: Vec3Like, alpha: number): Vec3Like {
  return Vec3.from(a).add(Vec3.from(b).sub(a).scale(alpha)).toObject();
}

function normalizeVec3(v: Vec3Like): Vec3Like {
  return Vec3.from(v).normalize().toObject();
}

function lerpNumber(a: number, b: number, alpha: number): number {
  return a + (b - a) * alpha;
}
